import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import InputBase from "@mui/material/InputBase";
import Tooltip from "@mui/material/Tooltip";
import { SxProps, Theme, alpha } from "@mui/material/styles";
import { useGridNavigation } from "./GridNavigationProvider";
import { GridPosition, gridMoveOfKey } from "./gridNavigation";

const ESCAPE_KEY = "Escape";
const CELL_HEIGHT = 30;
const CELL_MIN_WIDTH = 92;
const FOCUS_RING_OPACITY = 0.15;
const ERROR_SURFACE_OPACITY = 0.06;

export interface GridCellInputProps<Target> {
  /** Where the cell sits for Tab / Enter: its row and column index in the grid. */
  position: GridPosition;
  /** Handed back to `onCommit`, e.g. `{ branchCode, categoryCode }`, so no closure is needed per cell. */
  target: Target;
  /** What the cell shows (e.g. "1,564.61", or the rejected typing of a cell in error). */
  text: string;
  /**
   * Called once per edit when the cell is left (Tab, Enter, a click elsewhere) with text that
   * differs from `text`. Parsing and validation are the caller's: it answers with a new `text`
   * (and `hasError`).
   */
  onCommit: (target: Target, text: string) => void;
  ariaLabel: string;
  hasError?: boolean;
  /** Shown on hover, e.g. the system value under an overwrite, or the error message. */
  tooltip?: ReactNode;
  /** Extra look of the input, e.g. the amber of an overwritten value. */
  inputSx?: SxProps<Theme>;
}

/**
 * One editable cell of an Excel-like grid; render inside a `GridNavigationProvider`.
 *
 * Focus selects the whole value, so typing replaces it. Typing is kept in the cell (the table does
 * not re-render per key) and committed when the cell is left. Tab / Shift+Tab move along the row,
 * Enter / Shift+Enter / ↓ / ↑ along the column; Enter with no row below commits in place. Escape
 * drops the typing. Tab past the last cell leaves the grid as the browser would.
 */
export default function GridCellInput<Target>({
  position,
  target,
  text,
  onCommit,
  ariaLabel,
  hasError = false,
  tooltip = "",
  inputSx,
}: Readonly<GridCellInputProps<Target>>) {
  const navigation = useGridNavigation();
  const [typedText, setTypedText] = useState<string | null>(null);
  const elementRef = useRef<HTMLInputElement | null>(null);
  const isSelectPending = useRef(false);
  const { registerCell, unregisterCell } = navigation;
  const { row, column } = position;

  const inputRef = useCallback(
    (element: HTMLInputElement | null) => {
      if (element) registerCell({ row, column }, element);
      else if (elementRef.current) unregisterCell({ row, column }, elementRef.current);
      elementRef.current = element;
    },
    [registerCell, unregisterCell, row, column],
  );

  // After a commit in place or an Escape the value is re-rendered; select it again so the next
  // typing replaces it, as it does after a move.
  useLayoutEffect(() => {
    if (!isSelectPending.current) return;
    isSelectPending.current = false;
    elementRef.current?.select();
  }, [text, typedText]);

  const commit = () => {
    if (typedText !== null && typedText !== text) onCommit(target, typedText);
    setTypedText(null);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => setTypedText(event.target.value);
  const handleFocus = (event: FocusEvent<HTMLInputElement>) => event.target.select();

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ESCAPE_KEY) {
      isSelectPending.current = true;
      setTypedText(null);
      return;
    }
    const move = gridMoveOfKey(event.key, event.shiftKey);
    if (!move) return;
    const next = navigation.cellAt(position, move);
    if (next) {
      // Focusing the next cell blurs this one, which commits it.
      event.preventDefault();
      next.focus();
      return;
    }
    if (move === "DOWN" || move === "UP") {
      event.preventDefault();
      isSelectPending.current = true;
      commit();
    }
  };

  return (
    <Tooltip title={tooltip}>
      <InputBase
        inputRef={inputRef}
        value={typedText ?? text}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        error={hasError}
        inputProps={{ "aria-label": ariaLabel, "aria-invalid": hasError, inputMode: "decimal" }}
        sx={[
          {
            width: "100%",
            minWidth: CELL_MIN_WIDTH,
            height: CELL_HEIGHT,
            px: 0.75,
            border: 1,
            borderColor: "transparent",
            borderRadius: 1.5,
            fontSize: "0.875rem",
            fontVariantNumeric: "tabular-nums",
            "& input": { textAlign: "right", p: 0 },
            "&:hover": { borderColor: "divider", bgcolor: "background.paper" },
            "&.Mui-focused": {
              borderColor: "primary.main",
              bgcolor: "background.paper",
              boxShadow: (theme) =>
                `0 0 0 3px ${alpha(theme.palette.primary.main, FOCUS_RING_OPACITY)}`,
            },
            "&.Mui-error": {
              borderColor: "error.main",
              bgcolor: (theme) => alpha(theme.palette.error.main, ERROR_SURFACE_OPACITY),
            },
          },
          ...(Array.isArray(inputSx) ? inputSx : [inputSx]),
        ]}
      />
    </Tooltip>
  );
}
